import { Switch } from "@/components/ui/switch";
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  forwardRef,
  useImperativeHandle,
} from "react";
import { useNavigate } from "react-router-dom";
import Calendar from "@/components/feature/roster_wanted/Calendar";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { QUERY_KEYS } from "@/constants/QUERY_KEYS";
import httpClient from "@/config/axios";
import useThrottle from "@/hooks/useThrottle";
import { toast } from "@/components/toast/CustomToast";
import { useWantedConfigQuery } from "@/hooks/roster-wanted/useWantedConfigQuery";
import CustomModal from "@/components/modal/CustomModal";
import LoadingSpiner from "@/components/ui/LoadingSpiner";
import { useFullNurseListQuery } from "@hooks/useFullNurseList";
import { useWantedListQuery } from "@/pages/roster-wanted/hooks/useRosterWantedQueries";
import { useHolidayDatesQuery as useHolidaysQuery } from "@/hooks/useHolidayDatesQuery";
import type { RosterConfigObject } from "@/hooks/useRosterConfig";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

interface WantedConfig {
  year: number;
  month: number;
  target_date: string;
  shift_type: string;
  max_requests: number;
  is_active?: boolean;
}

interface ConfTab4Props {
  isAdmin: boolean;
  selectedGroupId: string | null;
  serverConfig: RosterConfigObject | undefined;
  visible?: boolean;
}

export interface ConfTab4Ref {
  hasUnsavedChanges: boolean;
  handleSave: (options?: { onSuccess?: () => void }) => void;
  handleReset: () => void;
}

function DetailSettingLink({
  onClick,
}: {
  onClick: (e: React.MouseEvent) => void;
}) {
  return (
    <span onClick={onClick} className="cursor-pointer">
      <span className="text-sig font-pretendard text-[13px] font-medium underline">
        상세설정
      </span>
    </span>
  );
}

interface ToggleSwitchProps {
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
  label?: string;
  disabled?: boolean;
}

function ToggleSwitch({
  checked,
  onCheckedChange,
  label,
  disabled,
}: ToggleSwitchProps) {
  return (
    <div className="flex items-center">
      {label && (
        <span className="text-text-main2 font-pretendard text-[16px] mr-auto">
          {label}
        </span>
      )}
      <div
        className={`flex items-center ${disabled ? "opacity-50 pointer-events-none" : ""}`}
      >
        <span className="text-[#949494] font-pretendard text-[10px]">
          사용안함
        </span>
        <div className="px-[5px]">
          <Switch
            checked={checked}
            onCheckedChange={onCheckedChange}
            disabled={disabled}
            className="h-[22px] w-[42px] data-[state=unchecked]:bg-[#D6D6DE]"
            thumbClassName="h-[18px] w-[18px] data-[state=checked]:translate-x-5"
          />
        </div>
        <span className="text-[#949494] font-pretendard text-[10px]">사용</span>
      </div>
    </div>
  );
}

const ConfTab4 = forwardRef<ConfTab4Ref, ConfTab4Props>(
  ({ isAdmin, selectedGroupId, visible }, ref) => {
    const queryClient = useQueryClient();
    const throttle = useThrottle();
    const navigate = useNavigate();

    // 현재 날짜를 기본값으로 사용
    const [activeDate, setActiveDate] = useState<{
      year: number;
      month: number;
    }>(() => {
      const now = new Date();
      return {
        year: now.getFullYear(),
        month: now.getMonth() + 1,
      };
    });

    // 간호사 목록 조회
    const { data: allNurses, isLoading: isNursesListLoading } =
      useFullNurseListQuery({
        isAdmin: isAdmin,
        groupId: selectedGroupId ?? undefined,
      });

    const nurses = useMemo(() => {
      return allNurses?.filter((n) => n.active !== 0) ?? [];
    }, [allNurses]);

    // 공휴일 조회
    const { data: holidayDates = [] } = useHolidaysQuery(
      activeDate.year,
      activeDate.month
    );

    const groupId = isAdmin ? (selectedGroupId ?? "") : "";

    // 원티드 목록 조회
    const { data: wantedList } = useWantedListQuery(groupId || undefined);

    // 원티드 목록이 있고 activeDate가 기본값일 때 첫 번째 항목으로 설정
    useEffect(() => {
      if (wantedList && wantedList.length > 0) {
        const firstWanted = wantedList[0];
        // 현재 activeDate가 기본값(현재 년월)이고 wantedList에 해당 날짜가 없으면 첫 번째 항목으로 설정
        const now = new Date();
        const isCurrentDate =
          activeDate.year === now.getFullYear() &&
          activeDate.month === now.getMonth() + 1;
        const existsInWantedList = wantedList.some(
          (w) => w.year === activeDate.year && w.month === activeDate.month
        );

        if (isCurrentDate && !existsInWantedList) {
          setActiveDate({
            year: firstWanted.year,
            month: firstWanted.month,
          });
        }
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [wantedList]);

    // 첫 번째 간호사의 설정값 가져오기 (타입 단언 사용)
    const firstNurse = nurses[0] as any;

    const [isAideEnabled, setIsAideEnabled] = useState(
      firstNurse?.enable_aide ?? false
    );
    const [isSecretEnabled, setIsSecretEnabled] = useState(
      firstNurse?.enable_nurse_pair_preference ?? false
    );
    const [wantedMaxRequests, setWantedMaxRequests] = useState<number | null>(
      firstNurse?.wanted_max_requests ?? null
    );
    const [isWantedLimitEnabled, setIsWantedLimitEnabled] = useState(
      firstNurse?.wanted_max_requests != null
    );
    const [dailyConfigs, setDailyConfigs] = useState<Record<string, number>>(
      {}
    );
    const [isDetailOpen, setIsDetailOpen] = useState(false);
    const [isTransitioning, setIsTransitioning] = useState(false);
    const [bulkApplyValue, setBulkApplyValue] = useState<number>(0);

    const [initialSettings, setInitialSettings] = useState({
      aide: firstNurse?.enable_aide ?? false,
      secret: firstNurse?.enable_nurse_pair_preference ?? false,
      maxRequests: firstNurse?.wanted_max_requests ?? null,
    });

    const initialConfigsRef = useRef<Record<string, number>>({});
    const initialDetailOpenRef = useRef(false);

    // 일자별 원티드 제한 설정 조회 (활성만 조회, include_inactive=false)
    const { data: configData, refetch: refetchConfig } = useWantedConfigQuery({
      year: activeDate?.year,
      month: activeDate?.month,
      groupId: groupId,
      enabled: !!activeDate && visible,
      includeInactive: false,
    });

    // UnsavedChangesModal 상태 관리
    const [unsavedModalConfig, setUnsavedModalConfig] = useState<{
      isOpen: boolean;
      title: string;
      onConfirm: () => void;
      onCancel: () => void;
      confirmText?: string;
      cancelText?: string;
    }>({
      isOpen: false,
      title: "",
      onConfirm: () => {},
      onCancel: () => {},
    });

    // 조회된 일별 설정 데이터 캘린더 상태에 반영 (is_active 없으면 응답 항목 전부 활성으로 표시)
    useEffect(() => {
      if (configData && Array.isArray(configData)) {
        const activeOnly = (configData as WantedConfig[]).filter(
          (item) => item.is_active !== false
        );
        const newConfigs: Record<string, number> = {};
        activeOnly.forEach((item) => {
          if (item.target_date && item.max_requests > 0) {
            newConfigs[item.target_date] = item.max_requests;
          }
        });
        setDailyConfigs(newConfigs);

        initialConfigsRef.current = newConfigs;
        const hasActiveConfigs = activeOnly.length > 0;
        initialDetailOpenRef.current = hasActiveConfigs;
        if (nurses.length > 0) {
          const firstNurseForInit = nurses[0] as any;
          setInitialSettings({
            aide: firstNurseForInit?.enable_aide ?? false,
            secret: firstNurseForInit?.enable_nurse_pair_preference ?? false,
            maxRequests: firstNurseForInit?.wanted_max_requests ?? null,
          });
        }

        if (hasActiveConfigs) {
          setIsDetailOpen(true);
        }
      }
    }, [configData, nurses]);

    // 간호사 데이터가 로드되면 초기값 설정
    useEffect(() => {
      if (nurses.length > 0) {
        const firstNurseForInit = nurses[0] as any;
        setIsAideEnabled(firstNurseForInit?.enable_aide ?? false);
        setIsSecretEnabled(
          firstNurseForInit?.enable_nurse_pair_preference ?? false
        );
        setWantedMaxRequests(firstNurseForInit?.wanted_max_requests ?? null);
        setIsWantedLimitEnabled(firstNurseForInit?.wanted_max_requests != null);
        setInitialSettings({
          aide: firstNurseForInit?.enable_aide ?? false,
          secret: firstNurseForInit?.enable_nurse_pair_preference ?? false,
          maxRequests: firstNurseForInit?.wanted_max_requests ?? null,
        });
      }
    }, [nurses]);

    useEffect(() => {
      setIsTransitioning(true);
      const timer = setTimeout(() => setIsTransitioning(false), 300);
      return () => clearTimeout(timer);
    }, [isDetailOpen, activeDate]);

    // '사용' 켤 때마다 최신 설정 다시 조회 (사용 안함 저장 후 켜면 비어 있어야 함)
    useEffect(() => {
      if (isDetailOpen && activeDate) {
        refetchConfig();
      }
    }, [isDetailOpen, activeDate, refetchConfig]);

    // 변경 사항 감지
    const hasChanges = useMemo(() => {
      const nurseChanged =
        isAideEnabled !== initialSettings.aide ||
        isSecretEnabled !== initialSettings.secret ||
        wantedMaxRequests !== initialSettings.maxRequests;
      if (nurseChanged) return true;

      const initialOpen = initialDetailOpenRef.current;
      const nowOpen = isDetailOpen;
      if (initialOpen && !nowOpen) return true;

      const initialDaily = initialConfigsRef.current;
      const currentDaily = dailyConfigs;
      const allKeys = new Set([
        ...Object.keys(initialDaily),
        ...Object.keys(currentDaily),
      ]);

      for (const key of allKeys) {
        const initVal = initialDaily[key] || 0;
        const currVal = currentDaily[key] || 0;
        if (initVal !== currVal) return true;
      }
      return false;
    }, [
      isAideEnabled,
      isSecretEnabled,
      wantedMaxRequests,
      initialSettings,
      isDetailOpen,
      dailyConfigs,
    ]);

    // 원티드 개별 요청 제한 인풋 유효성 검사
    const isInputInvalid =
      isWantedLimitEnabled &&
      (wantedMaxRequests === null || wantedMaxRequests < 0);

    const handleSaveSuccess = useCallback(
      async (savedWithDetailOff: boolean) => {
        if (!activeDate) return;

        await queryClient.invalidateQueries({
          queryKey: QUERY_KEYS.nurses.list(groupId),
        });
        const wantedConfigKey = QUERY_KEYS.wanted.config(
          activeDate.year,
          activeDate.month,
          groupId ?? ""
        );
        await queryClient.invalidateQueries({ queryKey: wantedConfigKey });
        await queryClient.refetchQueries({ queryKey: wantedConfigKey });
        if (savedWithDetailOff) {
          queryClient.setQueryData([...wantedConfigKey, false], []);
        }
        toast.success("저장되었습니다.");
      },
      [activeDate, groupId, queryClient]
    );

    const { mutate: saveSettings, isPending } = useMutation({
      mutationFn: async () => {
        if (!activeDate) return null;

        const promises: Promise<unknown>[] = [];

        // 1. 간호사 설정이 변경되었는지 확인
        const hasNurseChanges = nurses.some((nurse: any) => {
          const nurseData = nurse as any;
          return (
            nurseData.enable_aide !== isAideEnabled ||
            nurseData.enable_nurse_pair_preference !== isSecretEnabled ||
            nurseData.wanted_max_requests !== wantedMaxRequests
          );
        });

        // 변경사항이 있으면 간호사 설정 업데이트
        if (hasNurseChanges) {
          const updatedNurses = nurses.map((nurse: any) => ({
            ...nurse,
            enable_aide: isAideEnabled,
            enable_nurse_pair_preference: isSecretEnabled,
            wanted_max_requests: wantedMaxRequests,
          }));

          const url = groupId
            ? `/nurses/bulk-update?group_id=${encodeURIComponent(String(groupId))}`
            : `/nurses/bulk-update`;

          promises.push(httpClient.post(url, updatedNurses));
        }

        // 2. 일자별 원티드 제한
        if (!isDetailOpen) {
          const toggleParams = new URLSearchParams({
            year: String(activeDate.year),
            month: String(activeDate.month),
          });
          if (groupId) toggleParams.set("group_id", groupId);
          promises.push(
            httpClient.delete(
              `/wanted/config/toggle?${toggleParams.toString()}`
            )
          );
        }

        // 일자별 설정이 ON일 때: 보낼 목록만 POST (제거한 날짜는 body에서 제외). 빈 배열이어도 항상 POST.
        if (isDetailOpen) {
          const configsToPost = Object.entries(dailyConfigs)
            .filter(([, count]) => count > 0)
            .map(([date, count]) => ({
              year: activeDate?.year,
              month: activeDate?.month,
              target_date: date,
              shift_type: null,
              max_requests: count,
            }));
          const configUrl = groupId
            ? `/wanted/config?group_id=${encodeURIComponent(String(groupId))}`
            : `/wanted/config?year=${activeDate?.year}&month=${activeDate?.month}`;
          promises.push(httpClient.post(configUrl, configsToPost));
        }

        // 실제 전송할 API가 있을 때만 Promise.all 실행
        if (promises.length > 0) {
          return await Promise.all(promises);
        }

        return null;
      },
      onSuccess: async (_data, variables: { savedWithDetailOff: boolean }) => {
        await handleSaveSuccess(
          (variables as { savedWithDetailOff: boolean })?.savedWithDetailOff ??
            false
        );
      },
    });

    const handleAideToggle = (enabled: boolean) => {
      setIsAideEnabled(enabled);
      if (!enabled) {
        setIsSecretEnabled(false);
      }
    };

    const handleWantedLimitToggle = (enabled: boolean) => {
      setIsWantedLimitEnabled(enabled);
      if (enabled) {
        setWantedMaxRequests((prev) => (prev != null ? prev : 3));
      } else {
        setWantedMaxRequests(null);
      }
    };

    const handleConfigChange = (date: string, val: number) => {
      setDailyConfigs((prev) => ({ ...prev, [date]: val }));
    };

    const handleBulkApply = () => {
      if (!activeDate) return;

      const daysInMonth = new Date(
        activeDate.year,
        activeDate.month,
        0
      ).getDate();

      const newConfigs: Record<string, number> = { ...dailyConfigs };

      // 해당 월의 모든 날짜에 일괄 적용
      for (let day = 1; day <= daysInMonth; day++) {
        const dateStr = `${activeDate.year}-${String(activeDate.month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
        if (bulkApplyValue > 0) {
          newConfigs[dateStr] = bulkApplyValue;
        } else {
          // 0이면 해당 날짜 제거
          delete newConfigs[dateStr];
        }
      }

      setDailyConfigs(newConfigs);
    };

    const handleSave = useCallback(
      (options?: { onSuccess?: () => void }) => {
        throttle(() => {
          saveSettings(
            { savedWithDetailOff: !isDetailOpen },
            {
              onSuccess: () => {
                // handleSaveSuccess는 useMutation의 onSuccess에서 이미 호출됨
                options?.onSuccess?.();
              },
            }
          );
        });
      },
      [throttle, saveSettings, isDetailOpen]
    );

    const handleReset = useCallback(() => {
      if (nurses.length > 0) {
        const firstNurseForReset = nurses[0] as any;
        setIsAideEnabled(firstNurseForReset?.enable_aide ?? false);
        setIsSecretEnabled(
          firstNurseForReset?.enable_nurse_pair_preference ?? false
        );
        setWantedMaxRequests(firstNurseForReset?.wanted_max_requests ?? null);
        setIsWantedLimitEnabled(
          firstNurseForReset?.wanted_max_requests != null
        );
      }
      setDailyConfigs(initialConfigsRef.current);
      setIsDetailOpen(initialDetailOpenRef.current);
      if (nurses.length > 0) {
        const firstNurseForReset = nurses[0] as any;
        setInitialSettings({
          aide: firstNurseForReset?.enable_aide ?? false,
          secret: firstNurseForReset?.enable_nurse_pair_preference ?? false,
          maxRequests: firstNurseForReset?.wanted_max_requests ?? null,
        });
      }
    }, [nurses]);

    // ref를 통해 부모 컴포넌트에 노출
    useImperativeHandle(
      ref,
      () => ({
        hasUnsavedChanges: hasChanges,
        handleSave,
        handleReset,
      }),
      [hasChanges, handleSave, handleReset]
    );

    const handleDetailClick = (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      const targetHref = "/Head_nurse_management";

      if (hasChanges) {
        setUnsavedModalConfig({
          isOpen: true,
          title: "현재 설정을 저장하고\n근무자 관리 페이지로 이동합니다.",
          confirmText: "예",
          cancelText: "아니오",
          onConfirm: () => {
            handleSave({
              onSuccess: () => {
                setUnsavedModalConfig((prev) => ({ ...prev, isOpen: false }));
                navigate(targetHref);
              },
            });
          },
          onCancel: () => {
            setUnsavedModalConfig((prev) => ({ ...prev, isOpen: false }));
          },
        });
      } else {
        navigate(targetHref);
      }
    };

    if (isNursesListLoading) {
      return (
        <div className="flex justify-center items-center h-[200px] ">
          <LoadingSpiner msg=" 원티드 설정을 불러오는 중입니다..." />
        </div>
      );
    }

    return (
      <div className="flex flex-col items-center px-[22px] pt-3 pb-6 relative">
        {isPending && (
          <div className="absolute top-0 z-10 bg-zinc-50/50 left-0 w-full h-full flex justify-center items-center">
            <LoadingSpiner />
          </div>
        )}
        <div className="w-full max-w-[600px] border rounded-lg">
          <div className="w-full  ">
            {/* 항목 1 */}
            <div className="w-full px-10 flex items-center justify-between py-4  pr-[68px] border-b border-[#D6D6DE]">
              <div className="flex flex-col">
                <span className="text-text-main2 font-pretendard text-[16px]">
                  Aide 입력 기능 사용
                </span>
                <DetailSettingLink onClick={handleDetailClick} />
              </div>

              <ToggleSwitch
                checked={isAideEnabled}
                onCheckedChange={handleAideToggle}
              />
            </div>

            {/* 항목 2 */}
            <div className="w-full px-10 flex items-center justify-between py-4 pr-[68px] border-b border-[#D6D6DE]">
              <div className="flex flex-col">
                <span className={`text-text-main2 font-pretendard text-[16px]`}>
                  Aide 입력 시크릿 원티드 기능 사용
                </span>
                <DetailSettingLink onClick={handleDetailClick} />
              </div>

              <ToggleSwitch
                checked={isSecretEnabled}
                onCheckedChange={setIsSecretEnabled}
                disabled={!isAideEnabled}
              />
            </div>

            {/* 항목 3 */}
            <div className="w-full px-10 flex items-center justify-between py-4  pr-1 border-b border-[#D6D6DE]">
              <div className="flex flex-col">
                <span className="text-text-main2 font-pretendard text-[16px]">
                  신청 가능한 휴가·휴무
                </span>
                <DetailSettingLink onClick={handleDetailClick} />
              </div>

              <div className="flex">
                <div className="mr-[9px]">
                  <ToggleSwitch
                    checked={isWantedLimitEnabled}
                    onCheckedChange={handleWantedLimitToggle}
                  />
                </div>

                <div className="">
                  <input
                    type="number"
                    value={wantedMaxRequests ?? ""}
                    placeholder={isWantedLimitEnabled ? "" : "-"}
                    onChange={(e) =>
                      setWantedMaxRequests(
                        e.target.value === "" ? null : Number(e.target.value)
                      )
                    }
                    onFocus={(e) => e.target.select()}
                    disabled={!isWantedLimitEnabled}
                    className={`w-[40px] h-[26px] border ${isInputInvalid ? "border-text-holiday" : "border-[#D6D6DE]"} rounded-[5px] text-center font-pretendard text-[16px] outline-none text-text-sub
                    [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none
                    ${!isWantedLimitEnabled ? "bg-[#F0F0F2] !text-[#C4C4C4]" : "bg-white"}`}
                  />
                  <span className="font-pretendard text-[12px] text-[#949494] ml-[5px]">
                    개
                  </span>
                </div>
              </div>
            </div>

            {/* 항목 4 */}
            <div
              className={`w-full px-10 flex-col flex items-center justify-between py-4   ${isDetailOpen && " border-[#D6D6DE]"}`}
            >
              <div className="flex items-center justify-between  w-full pr-[28px]">
                <div>
                  <span className="text-text-main2 font-pretendard text-[16px]">
                    원티드 일자별 요청 제한
                  </span>
                  <span className="text-text-main2 font-pretendard text-[12px]">
                    (OFF,휴가,휴무)
                  </span>
                </div>

                <ToggleSwitch
                  checked={isDetailOpen}
                  onCheckedChange={() => setIsDetailOpen(!isDetailOpen)}
                />
              </div>

              {/* 날짜 선택 */}

              <div
                className={`w-full  grid transition-all duration-300 ease-in-out ${isDetailOpen && activeDate ? "grid-rows-[1fr] opacity-100 mt-4" : "grid-rows-[0fr] opacity-0 mt-0"}`}
              >
                <div
                  className={
                    isDetailOpen && activeDate && !isTransitioning
                      ? ""
                      : "overflow-hidden"
                  }
                >
                  {activeDate && (
                    <div className="flex flex-col justify-center items-center pb-5">
                      <div className="mt-5 flex justify-between items-center w-full max-w-[530px]">
                        {isDetailOpen && (
                          <div className="flex items-center  w-full pr-[28px]">
                            <Select
                              value={`${activeDate.year}-${activeDate.month}`}
                              onValueChange={(value) => {
                                const [year, month] = value
                                  .split("-")
                                  .map(Number);
                                setActiveDate({ year, month });
                              }}
                            >
                              <SelectTrigger className="w-[200px] h-[30px] text-sm">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {wantedList && wantedList.length > 0
                                  ? wantedList.map((item) => {
                                      const value = `${item.year}-${item.month}`;
                                      const isClosed = item.status === "closed";
                                      return (
                                        <SelectItem
                                          key={value}
                                          value={value}
                                          className={
                                            isClosed
                                              ? "opacity-60 text-[#939399] "
                                              : ""
                                          }
                                        >
                                          <span className="text-sm">
                                            {item.year}년 {item.month}월
                                            {isClosed && " (마감)"}
                                          </span>
                                        </SelectItem>
                                      );
                                    })
                                  : // 원티드 목록이 없을 때는 현재 연도 기준으로 표시
                                    Array.from({ length: 5 }, (_, yearIdx) => {
                                      const year =
                                        new Date().getFullYear() - 2 + yearIdx;
                                      return Array.from(
                                        { length: 12 },
                                        (_, monthIdx) => {
                                          const month = monthIdx + 1;
                                          const value = `${year}-${month}`;
                                          return (
                                            <SelectItem
                                              key={value}
                                              value={value}
                                            >
                                              <span className="text-sm">
                                                {year}년 {month}월
                                              </span>
                                            </SelectItem>
                                          );
                                        }
                                      );
                                    })}
                              </SelectContent>
                            </Select>
                          </div>
                        )}
                        <div className="flex gap-2 items-center">
                          <div className="flex items-center gap-1">
                            <Input
                              type="number"
                              className="w-[65px] text-sm!"
                              min={0}
                              max={99}
                              value={bulkApplyValue}
                              onChange={(e) =>
                                setBulkApplyValue(
                                  e.target.value === ""
                                    ? 0
                                    : Math.max(
                                        0,
                                        Math.min(99, Number(e.target.value))
                                      )
                                )
                              }
                            />
                            <span className="text-xs text-zinc-500">개</span>
                          </div>
                          <Button
                            onClick={handleBulkApply}
                            className="text-xs! p-2! h-[25px]! bg-sig hover:bg-[#0052CC]"
                          >
                            일괄적용
                          </Button>
                        </div>
                      </div>
                      <Calendar
                        activeDate={activeDate}
                        holidayDates={holidayDates}
                        dailyConfigs={dailyConfigs}
                        onConfigChange={handleConfigChange}
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="conf_schedule__flexRow mt-10">
          <button
            type="button"
            className="conf_schedule__button conf_schedule__button--save"
            onClick={() => handleSave()}
            disabled={!hasChanges || isPending}
          >
            {isPending ? "저장 중..." : "저장하기"}
          </button>
        </div>
        <CustomModal.Confirm
          isOpen={unsavedModalConfig.isOpen}
          onClose={unsavedModalConfig.onCancel}
          onConfirm={unsavedModalConfig.onConfirm}
          isPending={isPending}
          title={"현재 설정을 저장하고\n근무자 관리 페이지로 이동합니다."}
          pendingMsg="저장 중..."
          confirmText={unsavedModalConfig.confirmText}
          cancelText={unsavedModalConfig.cancelText}
        />
      </div>
    );
  }
);

ConfTab4.displayName = "ConfTab4";

export default ConfTab4;
